import { Link } from "react-router";
import {
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  Loader2,
  XCircle,
} from "lucide-react";
import type { RunSummary } from "@pinpoint/shared";
import { usePipelineContext } from "@/lib/pipeline";
import { FUNNEL_ORDER, STAGE_STYLES } from "@/lib/stages";
import { relativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/AppShell";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Dashboard (docs/02 §3.1) — the morning question: what happened, what needs
 * me, what's leaking?
 *
 * Every number on this screen is a link into the thing it counts. A dashboard
 * that reports without offering the next action just makes people go and find
 * the screen themselves.
 */
export function Dashboard() {
  const context = usePipelineContext();

  if (context.isPending) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </>
    );
  }

  const data = context.data;
  if (!data) return null;

  const pipeline = data.pipeline;
  const total = Object.values(pipeline).reduce((sum, n) => sum + n, 0);
  const peak = Math.max(1, ...FUNNEL_ORDER.map((s) => pipeline[s] ?? 0));

  const attention = [
    {
      label: "Awaiting qualification",
      count: data.pendingWork.awaitingQualification,
      to: "/pipeline?stage=sourced",
      hint: "New companies needing a keep-or-kill decision",
    },
    {
      label: "Drafts awaiting review",
      count: data.pendingWork.awaitingReview,
      to: "/review",
      hint: "Sequences a human must approve before sending",
    },
    {
      label: "Needs enrichment",
      count: data.pendingWork.needsEnrichment,
      to: "/pipeline?stage=qualified",
      hint: "Research missing or over 30 days old",
    },
    {
      label: "Approved, not sent",
      count: data.pendingWork.approvedNotSent,
      to: "/review",
      hint: "Ready for the sending runbook",
    },
  ];

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          total === 0
            ? "Nothing in the pipeline yet"
            : `${total} companies in the pipeline`
        }
      />

      <div className="flex flex-col gap-8 px-4 py-6 sm:px-6">
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-medium">Funnel</h2>
          {total === 0 ? (
            <EmptyPipeline />
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-11">
              {FUNNEL_ORDER.map((stage) => {
                const count = pipeline[stage] ?? 0;
                return (
                  <Link
                    key={stage}
                    to={`/pipeline?stage=${stage}`}
                    className="group flex flex-col gap-1.5 rounded-lg border p-2.5 transition hover:bg-muted/60"
                  >
                    <span className="truncate text-[11px] text-muted-foreground">
                      {STAGE_STYLES[stage].label}
                    </span>
                    <span className="text-lg font-semibold tabular-nums">
                      {count}
                    </span>
                    {/* A bar rather than a number alone: the shape of the
                        drop-off is the thing worth seeing at a glance. */}
                    <span className="h-1 w-full overflow-hidden rounded-full bg-muted">
                      <span
                        className={cn(
                          "block h-full rounded-full",
                          STAGE_STYLES[stage].dot,
                        )}
                        style={{ width: `${(count / peak) * 100}%` }}
                      />
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-medium">Needs attention</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {attention.map((card) => (
              <Link
                key={card.label}
                to={card.to}
                className={cn(
                  "flex flex-col gap-1 rounded-lg border p-4 transition hover:bg-muted/60",
                  card.count > 0 && "border-primary/40",
                )}
              >
                <span className="text-2xl font-semibold tabular-nums">
                  {card.count}
                </span>
                <span className="text-sm font-medium">{card.label}</span>
                <span className="text-xs text-muted-foreground">{card.hint}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Recent agent activity</h2>
            <Link
              to="/runs"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              All runs →
            </Link>
          </div>

          {data.recentRuns.length === 0 ? (
            <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No agent has run yet. Connect Claude to the MCP server and start a
              sourcing strategy.
            </p>
          ) : (
            <div className="divide-y overflow-hidden rounded-lg border">
              {data.recentRuns.map((run) => (
                <Link
                  key={run.id}
                  to={`/runs/${run.id}`}
                  className="block transition hover:bg-muted/60"
                >
                  <RunRow run={run} />
                </Link>
              ))}
            </div>
          )}
        </section>

        <ConfigWarnings settings={data.settings} />
      </div>
    </>
  );
}

export function RunStatusIcon({ status }: { status: RunSummary["status"] }) {
  switch (status) {
    case "running":
      return <Loader2 className="size-4 animate-spin text-primary" />;
    case "succeeded":
      return <CheckCircle2 className="size-4 text-emerald-500" />;
    case "failed":
      return <XCircle className="size-4 text-rose-500" />;
    default:
      return <CircleDashed className="size-4 text-muted-foreground" />;
  }
}

/**
 * Who ran this, and from where.
 *
 * The token name alone is not attribution — names repeat and get edited. The
 * owner is joined live through the token id, and a revoked token is called out
 * because a run under one is history that can no longer be continued.
 */
export function RunAttribution({ run }: { run: RunSummary }) {
  const parts: string[] = [run.startedByLabel];
  if (run.agentTokenOwner) parts.push(run.agentTokenOwner);
  if (run.startedFromIp) parts.push(run.startedFromIp);

  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      <span>{parts.join(" · ")}</span>
      {run.agentTokenRevoked ? (
        <span className="rounded bg-rose-500/15 px-1.5 text-[11px] text-rose-700 dark:text-rose-300">
          token revoked
        </span>
      ) : null}
    </span>
  );
}

export function RunRow({ run }: { run: RunSummary }) {
  // The stats an agent reported are the most informative thing about a run, so
  // they lead rather than hiding behind a click.
  const stats = run.stats
    ? Object.entries(run.stats)
        .map(([key, value]) => `${value} ${key.replace(/_/g, " ")}`)
        .join(" · ")
    : null;

  return (
    <div className="flex items-start gap-3 p-3">
      <span className="mt-0.5">
        <RunStatusIcon status={run.status} />
      </span>

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-medium">{run.strategyKey}</span>
          <span className="text-xs text-muted-foreground">
            v{run.strategyVersion}
          </span>
        </p>

        {stats ? (
          <p className="mt-0.5 text-sm text-muted-foreground">{stats}</p>
        ) : run.summaryMd ? (
          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
            {run.summaryMd}
          </p>
        ) : null}

        <p className="mt-1 text-xs text-muted-foreground">
          <RunAttribution run={run} /> · {relativeTime(run.startedAt)}
        </p>
      </div>
    </div>
  );
}

function EmptyPipeline() {
  return (
    <div className="rounded-lg border border-dashed p-6 text-center">
      <p className="text-sm font-medium">The pipeline is empty.</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        Companies appear here once an agent runs a sourcing strategy through the
        MCP server. See <code className="text-xs">docs/SETUP.md §6a</code> for
        how to connect Claude.
      </p>
    </div>
  );
}

/**
 * The ICP and reference-client list are inputs the sourcing strategies cannot
 * work without, and they seed empty. Saying so on the dashboard is cheaper than
 * letting someone discover it when a run comes back with nothing.
 */
function ConfigWarnings({ settings }: { settings: Record<string, unknown> }) {
  const clients = settings.reference_clients;
  const missing: string[] = [];

  if (!Array.isArray(clients) || clients.length === 0) {
    missing.push("the reference-client list");
  }
  const icp = settings.icp_definition as { markdown?: string } | undefined;
  if (!icp?.markdown || icp.markdown.includes("to be completed")) {
    missing.push("the ICP definition");
  }

  if (missing.length === 0) return null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
      <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="text-sm">
        <p className="font-medium">Setup incomplete</p>
        <p className="mt-0.5 text-muted-foreground">
          Sourcing strategies read {missing.join(" and ")} from settings, and
          they are still empty. Competitor Mining will refuse to run rather than
          invent a client list.
        </p>
      </div>
    </div>
  );
}
